import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Input, SimpleTable, useToast, Toggle, } from '@src/components/ui';
import AdminLayout from '../AdminLayout';
import styles from './ProductDetails.module.scss';
import { uploadImage, associateImageWithProduct, setPrimaryImage } from '../../../api/ProductApi';
import ActionMenu from '../../../components/ActionMenu';
import findIndex from "lodash/findIndex";
import { fetchProductCategoryTree } from '../../../api/ProductCategoryApi';
import ImageUploader from '../../../components/ImageUploader';
const NEW_GROUP_PRICE_ID = "NEW_GROUP_PRICE_ID";
/**
 * Validates the Shipping Amount field. Blank is valid - it means "fall back to the
 * manufacturer default, then the shipping method price" (see Product.shipping_amount
 * help text on the backend). Only non-numeric and negative values are errors.
 */
export const validateShippingAmount = (value) => {
    const trimmed = value.trim();
    if (trimmed === '')
        return null;
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed))
        return 'Shipping amount must be a number';
    if (parsed < 0)
        return 'Shipping amount cannot be negative';
    return null;
};
const getApiValue = (data, ...keys) => {
    for (const key of keys) {
        if (Object.prototype.hasOwnProperty.call(data, key) && data[key] !== null && data[key] !== undefined) {
            return data[key];
        }
    }
    return null;
};
const toFormString = (value, fallback = '') => {
    if (value === null || value === undefined) {
        return fallback;
    }
    return String(value);
};
const getCategoryValue = (data) => {
    return (getApiValue(data, 'categoryId', 'category_id', 'categoryRef', 'category_ref') ||
        data.categoryRefData?.id ||
        data.category_ref_data?.id ||
        data.category ||
        '');
};
const buildProductFormData = (data) => ({
    title: toFormString(getApiValue(data, 'title')),
    sku: toFormString(getApiValue(data, 'sku')),
    description: toFormString(getApiValue(data, 'description')),
    price: toFormString(getApiValue(data, 'price')),
    cost: toFormString(getApiValue(data, 'cost')),
    shippingAmount: toFormString(getApiValue(data, 'shippingAmount', 'shipping_amount')),
    stockQuantity: toFormString(getApiValue(data, 'stockQuantity', 'stock_quantity'), '0'),
    category: toFormString(getCategoryValue(data)),
    packSize: toFormString(getApiValue(data, 'packSize', 'pack_size')),
    unitsPerPack: toFormString(getApiValue(data, 'unitsPerPack', 'units_per_pack')),
    images: Array.isArray(data.images) ? data.images : [],
    groupPrices: [],
});
const buildManufacturerFormData = (data) => ({
    mfrName: toFormString(getApiValue(data, 'manufacturer')),
    mfrSku: toFormString(getApiValue(data, 'manufacturerSku', 'manufacturer_sku')),
    mfrDescription: toFormString(getApiValue(data, 'manufacturerDescription', 'manufacturer_description')),
    mfrWholesalePrice: toFormString(getApiValue(data, 'manufacturerWholesalePrice', 'manufacturer_wholesale_price')),
    mfrRetailPrice: toFormString(getApiValue(data, 'manufacturerRetailPrice', 'manufacturer_retail_price')),
    mfrPackSize: toFormString(getApiValue(data, 'manufacturerPackSize', 'manufacturer_pack_size')),
    mfrUnitsPerPack: toFormString(getApiValue(data, 'manufacturerUnitsPerPack', 'manufacturer_units_per_pack')),
});
const flattenCategoryTree = (categories, depth = 0) => {
    const sorted = [...categories].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    return sorted.flatMap((category) => {
        const current = [{ ...category, depth }];
        const children = category.children?.length
            ? flattenCategoryTree(category.children, depth + 1)
            : [];
        return [...current, ...children];
    });
};
const ProductDetails = () => {
    const deleteImage = async (productId, imageId) => {
        const csrfToken = getCsrfToken();
        const response = await fetch(`/api/v1/products/${productId}/delete-image/${imageId}/`, {
            method: 'DELETE',
            headers: {
                'X-CSRFToken': csrfToken || '',
            },
            credentials: 'include',
        });
        if (!response.ok) {
            throw new Error('Failed to delete image');
        }
        return await response.json(); // Should include updated images array
    };
    const { productId } = useParams();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const isNewProduct = !productId || productId === 'new';
    const [formData, setFormData] = useState({
        title: '',
        sku: '',
        description: '',
        price: '',
        cost: '',
        shippingAmount: '',
        stockQuantity: "0",
        category: '',
        packSize: '',
        unitsPerPack: '',
        images: [],
        groupPrices: [],
    });
    const [mfrFormData, setMfrFormData] = useState({
        mfrName: '',
        mfrSku: '',
        mfrDescription: '',
        mfrWholesalePrice: '',
        mfrRetailPrice: '',
        mfrPackSize: '',
        mfrUnitsPerPack: '',
    });
    const [newGroupPriceFormData, setNewGroupPriceFormData] = useState({
        id: NEW_GROUP_PRICE_ID,
        corpBuyingGroup: '',
        price: '',
        corpDisc: false,
    });
    const [isSaving, setIsSaving] = useState(false);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [productStats, setProductStats] = useState({
        unitsThisMonth: 0,
        revenueThisMonth: 0,
        avgOrderValue: 0,
    });
    const [editingPriceId, setEditingPriceId] = useState(null);
    const [editingPriceValue, setEditingPriceValue] = useState('');
    const [categoryTree, setCategoryTree] = useState([]);
    const categoryOptions = useMemo(() => flattenCategoryTree(categoryTree), [categoryTree]);
    const formValidation = useMemo(() => {
        const errors = [];
        if (!formData.title.trim())
            errors.push('Name is required');
        if (!formData.sku.trim())
            errors.push('SKU is required');
        if (!formData.category.trim())
            errors.push('Category is required');
        if (!formData.price || parseFloat(formData.price) <= 0)
            errors.push('Price must be greater than 0');
        if (parseFloat(formData.stockQuantity) < 0)
            errors.push('Stock quantity must be 0 or greater');
        const shippingAmountError = validateShippingAmount(formData.shippingAmount);
        if (shippingAmountError)
            errors.push(shippingAmountError);
        if (errors.length > 0) {
            return { isFormValid: false, errors };
        }
        return { isFormValid: true, errors: null };
    }, [formData]);
    const { isFormValid, errors } = formValidation;
    let imagesToRender = (isNewProduct ? formData.images : product?.images) || [];
    // If not new, sort so primary image is always first (only for ProductImage objects)
    if (!isNewProduct && Array.isArray(imagesToRender) && imagesToRender.length > 1) {
        const files = imagesToRender.filter(img => img instanceof File);
        const productImages = imagesToRender.filter(img => !(img instanceof File));
        productImages.sort((a, b) => {
            const aPrimary = ('isPrimary' in a ? a.isPrimary : a.is_primary) ? 1 : 0;
            const bPrimary = ('isPrimary' in b ? b.isPrimary : b.is_primary) ? 1 : 0;
            return bPrimary - aPrimary;
        });
        imagesToRender = [...productImages, ...files];
    }
    const selectedCategory = formData.category;
    const getSelectedCategoryLabel = () => {
        if (!selectedCategory)
            return 'Select Category';
        const selectedOption = categoryOptions.find((category) => category.id === selectedCategory);
        if (selectedOption) {
            return selectedOption.name;
        }
        return selectedCategory;
    };
    const fetchProductStats = React.useCallback(async () => {
        try {
            const response = await fetch(`/api/v1/products/${productId}/stats/`, {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setProductStats({
                    unitsThisMonth: data.units_sold_this_month || 0,
                    revenueThisMonth: data.revenue_this_month || 0,
                    avgOrderValue: data.avg_order_value || 0,
                });
            }
        }
        catch (err) {
            console.error('Error fetching product stats:', err);
        }
    }, [productId]);
    const fetchGroupPrices = React.useCallback(async (fd) => {
        try {
            const response = await fetch(`/api/v1/products/${productId}/group-prices/`, {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setFormData({
                    ...fd,
                    groupPrices: data.results || data
                });
            }
        }
        catch (err) {
            console.error('Error fetching group prices:', err);
        }
    }, [productId]);
    useEffect(() => {
        const loadCategories = async () => {
            try {
                const response = await fetchProductCategoryTree();
                const rawData = response.body;
                const treeData = Array.isArray(rawData) ? rawData : rawData.results || [];
                setCategoryTree(treeData);
            }
            catch (err) {
                console.error('Error fetching product categories:', err);
            }
        };
        loadCategories();
    }, []);
    useEffect(() => {
        const fetchProduct = async () => {
            if (isNewProduct) {
                setLoading(false);
                return;
            }
            try {
                setLoading(true);
                const response = await fetch(`/api/v1/products/${productId}/`, {
                    credentials: 'include',
                });
                if (!response.ok) {
                    throw new Error('Failed to fetch product');
                }
                const data = await response.json();
                setProduct(data);
                const normalizedFormData = buildProductFormData(data);
                setMfrFormData(buildManufacturerFormData(data));
                setFormData(normalizedFormData);
                fetchProductStats();
                fetchGroupPrices(normalizedFormData);
            }
            catch (err) {
                setError('Failed to load product details');
                console.error(err);
            }
            finally {
                setLoading(false);
            }
        };
        fetchProduct();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId, isNewProduct, fetchGroupPrices, fetchProductStats]);
    useEffect(() => {
        if (!formData.category || !categoryOptions.length) {
            return;
        }
        const hasSelectedCategory = categoryOptions.some((category) => category.id === formData.category);
        if (hasSelectedCategory) {
            return;
        }
        const fallbackByName = categoryOptions.find((category) => category.name.toLowerCase() === formData.category.toLowerCase());
        if (fallbackByName) {
            setFormData((prev) => ({
                ...prev,
                category: fallbackByName.id,
            }));
        }
    }, [categoryOptions, formData.category]);
    const generateSlug = (title) => {
        return title
            .toLowerCase()
            .replace(/[^a-z0-9 -]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .trim();
    };
    const handleCreateProduct = async () => {
        if (!isFormValid && errors) {
            showToast({
                message: 'Please fix the following errors: ' + errors.join(', '),
                variant: 'error',
                noBorder: true,
            });
            return;
        }
        setIsSaving(true);
        try {
            const { images, groupPrices, ...productFormData } = formData;
            const selectedCategoryNode = categoryOptions.find((category) => category.id === productFormData.category);
            const requestData = {
                ...productFormData,
                category: selectedCategoryNode?.name || null,
                category_id: selectedCategoryNode?.id || null,
                packSize: productFormData.packSize || null,
                unitsPerPack: productFormData.unitsPerPack || null,
                shippingAmount: productFormData.shippingAmount || null,
                stockQuantity: formData.stockQuantity || 0,
                path: generateSlug(formData.title),
                manufacturer: mfrFormData.mfrName || null,
                manufacturerSku: mfrFormData.mfrSku || null,
                manufacturerDescription: mfrFormData.mfrDescription || null,
                manufacturerRetailPrice: mfrFormData.mfrRetailPrice || null,
                manufacturerWholesalePrice: mfrFormData.mfrWholesalePrice || null,
                manufacturerPackSize: mfrFormData.mfrPackSize || null,
                manufacturerUnitsPerPack: mfrFormData.mfrUnitsPerPack || null,
            };
            const csrfToken = getCsrfToken();
            const response = await fetch('/api/v1/products/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken || '',
                },
                credentials: 'include',
                body: JSON.stringify(requestData),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || errorData.message || 'Failed to create product');
            }
            const createdProduct = await response.json();
            const newProductId = createdProduct.id;
            // --- Prepare image upload requests using generic API ---
            const fileImages = images.filter(i => i instanceof File);
            const imageRequests = fileImages.map(async (file) => {
                // 1. Upload image to generic API
                const imageData = await uploadImage(file);
                // 2. Associate image with product
                return associateImageWithProduct(newProductId, imageData.id);
            });
            // --- Prepare group price creation requests ---
            const groupPriceRequests = groupPrices.map((group) => fetch(`/api/v1/products/${newProductId}/group-prices/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken || '',
                },
                credentials: 'include',
                body: JSON.stringify(group),
            }));
            // --- Run all requests in parallel ---
            const allRequests = [...imageRequests, ...groupPriceRequests];
            if (allRequests.length > 0) {
                const results = await Promise.allSettled(allRequests);
                const failed = results.filter((r) => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.ok));
                if (failed.length > 0) {
                    console.warn(`Some requests failed: ${failed.length}`);
                    showToast({
                        message: `Product created, but ${failed.length} related items failed to save.`,
                        variant: 'warning',
                        noBorder: true,
                    });
                }
            }
            showToast({
                message: 'Product created successfully!',
                variant: 'success',
                noBorder: true,
            });
            navigate(`/web-admin/products/${newProductId}`);
        }
        catch (err) {
            console.error('Error creating product:', err);
            showToast({
                message: 'Failed to create product',
                variant: 'error',
                noBorder: true,
            });
        }
        finally {
            setIsSaving(false);
        }
    };
    const handleSaveChanges = async () => {
        if (!isFormValid && errors) {
            showToast({
                message: 'Please fix the following errors: ' + errors.join(', '),
                variant: 'error',
                noBorder: true,
            });
            return;
        }
        setIsSaving(true);
        try {
            const selectedCategoryNode = categoryOptions.find((category) => category.id === formData.category);
            const requestData = {
                title: formData.title,
                sku: formData.sku,
                description: formData.description,
                price: parseFloat(formData.price),
                stock_quantity: formData.stockQuantity ? parseInt(formData.stockQuantity) : 0,
                category: selectedCategoryNode?.name || null,
                category_id: selectedCategoryNode?.id || null,
                path: generateSlug(formData.title),
                is_available: true,
            };
            const url = isNewProduct ? '/api/v1/products/' : `/api/v1/products/${productId}/`;
            const method = isNewProduct ? 'POST' : 'PATCH';
            const csrfToken = getCsrfToken();
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken || '',
                },
                credentials: 'include', // Include session cookies
                body: JSON.stringify(requestData),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || errorData.message || 'Failed to save product');
            }
            const savedProduct = await response.json();
            if (isNewProduct) {
                showToast({
                    message: 'Product created successfully!',
                    variant: 'success',
                    noBorder: true,
                });
                navigate(`/web-admin/products/${savedProduct.id}`);
            }
            else {
                setProduct(savedProduct);
                showToast({
                    message: 'Changes saved successfully!',
                    variant: 'success',
                    noBorder: true,
                });
            }
        }
        catch (err) {
            console.error('Error saving product:', err);
            showToast({
                message: `Failed to save product: ${err instanceof Error ? err.message : 'Unknown error'}`,
                variant: 'error',
                noBorder: true,
            });
        }
        finally {
            setIsSaving(false);
        }
    };
    const handleSaveManufacturerDetails = async () => {
        setIsSaving(true);
        try {
            const response = await fetch(`/api/v1/products/${productId}/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': getCsrfToken() || '',
                },
                credentials: 'include', // Include session cookies
                body: JSON.stringify({
                    manufacturer: mfrFormData.mfrName,
                    manufacturer_sku: mfrFormData.mfrSku,
                    manufacturer_description: mfrFormData.mfrDescription,
                    manufacturer_wholesale_price: mfrFormData.mfrWholesalePrice
                        ? parseFloat(mfrFormData.mfrWholesalePrice)
                        : null,
                    manufacturer_retail_price: mfrFormData.mfrRetailPrice
                        ? parseFloat(mfrFormData.mfrRetailPrice)
                        : null,
                    manufacturer_pack_size: mfrFormData.mfrPackSize
                        ? parseInt(mfrFormData.mfrPackSize)
                        : null,
                    manufacturer_units_per_pack: mfrFormData.mfrUnitsPerPack
                        ? parseInt(mfrFormData.mfrUnitsPerPack)
                        : null,
                }),
            });
            if (!response.ok) {
                throw new Error('Failed to save manufacturer details');
            }
            const updatedProduct = await response.json();
            setProduct(updatedProduct);
            showToast({
                message: 'Manufacturer details saved successfully!',
                variant: 'success',
                noBorder: true,
            });
        }
        catch (err) {
            console.error('Error saving manufacturer details:', err);
            showToast({
                message: 'Failed to save manufacturer details',
                variant: 'error',
                noBorder: true,
            });
        }
        finally {
            setIsSaving(false);
        }
    };
    const handleStatusChange = async (newStatus) => {
        try {
            const csrfToken = getCsrfToken();
            const response = await fetch(`/api/v1/products/${productId}/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken || '',
                },
                credentials: 'include',
                body: JSON.stringify({
                    is_available: newStatus === 'published',
                }),
            });
            if (!response.ok) {
                throw new Error(`Failed to update status: ${response.status} ${response.statusText}`);
            }
            const updatedProduct = await response.json();
            setProduct(prev => ({
                ...prev,
                ...updatedProduct,
                images: prev?.images || updatedProduct.images || []
            }));
        }
        catch (err) {
            showToast({
                message: `Failed to update status: ${err instanceof Error ? err.message : 'Unknown error'}`,
                variant: 'error',
                noBorder: true,
            });
        }
    };
    const handleSavePricing = async () => {
        const shippingAmountError = validateShippingAmount(formData.shippingAmount);
        if (shippingAmountError) {
            showToast({ message: shippingAmountError, variant: 'error', noBorder: true });
            return;
        }
        setIsSaving(true);
        try {
            const response = await fetch(`/api/v1/products/${productId}/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': getCsrfToken() || '',
                },
                credentials: 'include', // Include session cookies
                body: JSON.stringify({
                    price: formData.price
                        ? parseFloat(formData.price)
                        : null,
                    cost: formData.cost
                        ? parseFloat(formData.cost)
                        : null,
                    shipping_amount: formData.shippingAmount.trim()
                        ? parseFloat(formData.shippingAmount)
                        : null,
                    stockQuantity: formData.stockQuantity,
                    packSize: formData.packSize || null,
                    unitsPerPack: formData.unitsPerPack || null,
                }),
            });
            if (!response.ok) {
                throw new Error('Failed to save pricing');
            }
            const updatedProduct = await response.json();
            setProduct({
                ...product,
                ...updatedProduct
            });
            showToast({
                message: 'Pricing saved successfully!',
                variant: 'success',
                noBorder: true,
            });
        }
        catch (err) {
            console.error('Error saving pricing:', err);
            showToast({
                message: 'Failed to save pricing',
                variant: 'error',
                noBorder: true,
            });
        }
        finally {
            setIsSaving(false);
        }
    };
    const handleCategoryChange = async (newCategoryId) => {
        const selectedNode = categoryOptions.find((category) => category.id === newCategoryId);
        setFormData((prev) => ({
            ...prev,
            category: newCategoryId,
        }));
        if (isNewProduct) {
            return;
        }
        else {
            try {
                const response = await fetch(`/api/v1/products/${productId}/`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': getCsrfToken() || '',
                    },
                    credentials: 'include',
                    body: JSON.stringify({
                        category: selectedNode?.name || null,
                        category_id: newCategoryId || null,
                    }),
                });
                if (!response.ok) {
                    throw new Error('Failed to update category');
                }
                const updatedProduct = await response.json();
                setFormData((prev) => ({
                    ...prev,
                    category: toFormString(getCategoryValue(updatedProduct), newCategoryId || ''),
                }));
                setProduct(prev => ({
                    ...prev,
                    ...updatedProduct,
                    images: prev?.images || updatedProduct.images || []
                }));
                showToast({
                    message: 'Category updated successfully!',
                    variant: 'success',
                    noBorder: true,
                });
            }
            catch (err) {
                console.error('Error updating category:', err);
                showToast({
                    message: 'Failed to update category',
                    variant: 'error',
                    noBorder: true,
                });
            }
        }
    };
    const getCsrfToken = () => {
        const cookies = document.cookie.split(';');
        for (const cookie of cookies) {
            const [name, value] = cookie.trim().split('=');
            if (name === 'csrftoken') {
                return value;
            }
        }
        return null;
    };
    const handleImageUpload = async (file) => {
        if (isNewProduct) {
            const updatedImages = [...formData.images];
            updatedImages.push(file);
            setFormData({
                ...formData,
                images: updatedImages,
            });
        }
        else {
            setIsUploadingImage(true);
            try {
                // 1. Upload image to generic API
                const imageData = await uploadImage(file);
                // 2. Associate image with product
                const updatedProduct = await associateImageWithProduct(productId, imageData.id);
                setProduct(updatedProduct);
                showToast({
                    message: 'Image uploaded successfully!',
                    variant: 'success',
                    noBorder: true,
                });
            }
            catch (err) {
                console.error('Error uploading image:', err);
                showToast({
                    message: 'Failed to upload image',
                    variant: 'error',
                    noBorder: true,
                });
            }
            finally {
                setIsUploadingImage(false);
            }
        }
    };
    const handleImageDelete = (imageIndex) => async (e) => {
        if (isNewProduct) {
            const updatedImages = [...formData.images];
            updatedImages.splice(imageIndex, 1);
            setFormData({
                ...formData,
                images: updatedImages,
            });
        }
        else {
            e.stopPropagation();
            const imageId = product?.images ? product?.images[imageIndex].id : null;
            if (!imageId)
                return;
            try {
                const csrfToken = getCsrfToken();
                const response = await fetch(`/api/v1/products/${productId}/delete-image/${imageId}/`, {
                    method: 'DELETE',
                    headers: {
                        'X-CSRFToken': csrfToken || '',
                    },
                    credentials: 'include',
                });
                if (!response.ok) {
                    throw new Error('Failed to delete image');
                }
                // Remove image from product state
                setProduct((prev) => {
                    if (!prev)
                        return prev;
                    return {
                        ...prev,
                        images: prev.images?.filter((img, idx) => idx !== imageIndex) || [],
                    };
                });
                showToast({
                    message: 'Image deleted successfully!',
                    variant: 'success',
                    noBorder: true,
                });
            }
            catch (err) {
                console.error('Error deleting image:', err);
                showToast({
                    message: 'Failed to delete image',
                    variant: 'error',
                    noBorder: true,
                });
            }
        }
    };
    const handleAddGroupPrice = async () => {
        const groupData = {
            ...newGroupPriceFormData,
            id: isNewProduct ? `${NEW_GROUP_PRICE_ID}_${formData.groupPrices.length}` : null,
        };
        if (isNewProduct) {
            const updatedGroupPrices = [...formData.groupPrices];
            updatedGroupPrices.push(groupData);
            setFormData({
                ...formData,
                groupPrices: updatedGroupPrices,
            });
            setNewGroupPriceFormData({
                id: NEW_GROUP_PRICE_ID,
                corpBuyingGroup: '',
                price: '',
                corpDisc: false,
            });
        }
        else {
            try {
                const response = await fetch(`/api/v1/products/${productId}/group-prices/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': getCsrfToken() || '',
                    },
                    credentials: 'include',
                    body: JSON.stringify(groupData),
                });
                if (!response.ok) {
                    throw new Error('Failed to add group price');
                }
                fetchGroupPrices(formData);
                setNewGroupPriceFormData({
                    id: NEW_GROUP_PRICE_ID,
                    corpBuyingGroup: '',
                    price: '',
                    corpDisc: false,
                });
            }
            catch (err) {
                console.error('Error adding group price:', err);
                showToast({
                    message: 'Failed to add group price',
                    variant: 'error',
                    noBorder: true,
                });
            }
        }
    };
    const handleEditGroupPrice = async (groupId, priceData) => {
        if (isNewProduct) {
            const groupPriceIndex = findIndex(formData.groupPrices, { id: groupId });
            if (groupPriceIndex !== -1) {
                const updatedGroupPrices = [...formData.groupPrices];
                updatedGroupPrices[groupPriceIndex].price = priceData.price;
                setFormData({
                    ...formData,
                    groupPrices: updatedGroupPrices,
                });
            }
        }
        else {
            try {
                const response = await fetch(`/api/v1/products/${productId}/group-prices/${groupId}/`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': getCsrfToken() || '',
                    },
                    credentials: 'include', // Include session cookies
                    body: JSON.stringify(priceData),
                });
                if (!response.ok) {
                    throw new Error('Failed to update group price');
                }
                fetchGroupPrices(formData);
            }
            catch (err) {
                console.error('Error updating group price:', err);
                showToast({
                    message: 'Failed to update group price',
                    variant: 'error',
                    noBorder: true,
                });
            }
        }
    };
    const handleDeleteGroupPrice = async (groupId) => {
        if (isNewProduct) {
            const groupPriceIndex = findIndex(formData.groupPrices, { id: groupId });
            if (groupPriceIndex !== -1) {
                const updatedGroupPrices = [...formData.groupPrices];
                updatedGroupPrices.splice(groupPriceIndex, 1);
                setFormData({
                    ...formData,
                    groupPrices: updatedGroupPrices,
                });
            }
        }
        else {
            try {
                const response = await fetch(`/api/v1/products/${productId}/group-prices/${groupId}/`, {
                    method: 'DELETE',
                    credentials: 'include', // Include session cookies
                });
                if (!response.ok) {
                    throw new Error('Failed to delete group price');
                }
                fetchGroupPrices(formData);
            }
            catch (err) {
                console.error('Error deleting group price:', err);
                showToast({
                    message: 'Failed to delete group price',
                    variant: 'error',
                    noBorder: true,
                });
            }
        }
    };
    if (loading)
        return (_jsx(AdminLayout, { children: _jsx("div", { children: "Loading..." }) }));
    if (error)
        return (_jsx(AdminLayout, { children: _jsx("div", { children: error }) }));
    if (!product && !isNewProduct)
        return (_jsx(AdminLayout, { children: _jsx("div", { children: "Product not found" }) }));
    return (_jsx(AdminLayout, { children: _jsxs("div", { className: styles.container, children: [_jsx("div", { className: styles.breadcrumbRow, children: _jsxs("div", { className: styles.breadcrumbs, children: [_jsx("span", { className: styles.backLink, onClick: () => navigate('/web-admin/products'), children: "Back to Products" }), _jsx("span", { className: styles.separator, children: "/" }), _jsx("span", { className: styles.current, children: product?.title || "New Product" })] }) }), _jsxs("div", { className: styles.titleRow, children: [_jsx("h1", { children: product?.title || "New Product" }), isNewProduct && (_jsx(Button, { variant: "primaryV2", size: "small", label: "Create", onClick: handleCreateProduct, disabled: isSaving || !isFormValid }))] }), !isNewProduct && (_jsxs("div", { className: styles.statsRow, children: [_jsxs("div", { className: `${styles.statBox} ${styles.green}`, children: [_jsx("div", { className: styles.statNumber, children: productStats.unitsThisMonth }), _jsx("div", { className: styles.statLabel, children: "Units Sold This Month" })] }), _jsxs("div", { className: `${styles.statBox} ${styles.blue}`, children: [_jsxs("div", { className: styles.statNumber, children: ["$", productStats.revenueThisMonth.toLocaleString()] }), _jsx("div", { className: styles.statLabel, children: "Revenue This Month" })] }), _jsxs("div", { className: `${styles.statBox} ${styles.brown}`, children: [_jsxs("div", { className: styles.statNumber, children: ["$", productStats.avgOrderValue.toFixed(2)] }), _jsx("div", { className: styles.statLabel, children: "Avg Order Value" })] })] })), _jsxs("div", { className: styles.mainContent, children: [_jsxs("div", { className: styles.leftSection, children: [_jsxs("div", { className: styles.formSection, children: [_jsx("h2", { className: styles.formTitle, children: "Product Basics" }), _jsxs("div", { className: styles.formContent, children: [_jsx("div", { className: styles.formField, children: _jsx(Input, { label: "Name", value: formData.title, onChange: (value) => setFormData({ ...formData, title: value }), width: "600px" }) }), _jsx("div", { className: styles.formField, children: _jsx(Input, { label: "SKU", value: formData.sku, onChange: (value) => setFormData({ ...formData, sku: value }), width: "600px" }) }), _jsxs("div", { className: styles.formField, children: [_jsx("label", { className: styles.textareaLabel, children: "Description" }), _jsx("textarea", { className: styles.textarea, value: formData.description, onChange: (e) => setFormData({ ...formData, description: e.target.value }), rows: 4 })] }), !isNewProduct && (_jsx("div", { className: styles.formActions, children: _jsx(Button, { variant: "primaryV2", size: "small", label: "Save Changes", onClick: handleSaveChanges, disabled: isSaving }) }))] })] }), _jsxs("div", { className: styles.formSection, children: [_jsx("h2", { className: styles.formTitle, children: "Manufacturer Details" }), _jsxs("div", { className: styles.formContent, children: [_jsx("div", { className: styles.formField, children: _jsx(Input, { label: "Mfr Name", value: mfrFormData.mfrName, onChange: (value) => setMfrFormData({ ...mfrFormData, mfrName: value }), width: "600px" }) }), _jsx("div", { className: styles.formField, children: _jsx(Input, { label: "Mfr SKU", value: mfrFormData.mfrSku, onChange: (value) => setMfrFormData({ ...mfrFormData, mfrSku: value }), width: "600px" }) }), _jsxs("div", { className: styles.formField, children: [_jsx("label", { className: styles.textareaLabel, children: "Mfr Description" }), _jsx("textarea", { className: styles.textarea, value: mfrFormData.mfrDescription, onChange: (e) => setMfrFormData({ ...mfrFormData, mfrDescription: e.target.value }), rows: 4 })] }), _jsxs("div", { className: styles.twoColumnInputs, children: [_jsx(Input, { label: "Mfr Wholesale Price", value: mfrFormData.mfrWholesalePrice, onChange: (value) => setMfrFormData({ ...mfrFormData, mfrWholesalePrice: value }), width: "100%", type: "number" }), _jsx(Input, { label: "Mfr Retail Price", value: mfrFormData.mfrRetailPrice, onChange: (value) => setMfrFormData({ ...mfrFormData, mfrRetailPrice: value }), width: "100%", type: "number" })] }), _jsxs("div", { className: styles.twoColumnInputs, children: [_jsx(Input, { label: "Mfr Pack Size", value: mfrFormData.mfrPackSize, onChange: (value) => setMfrFormData({ ...mfrFormData, mfrPackSize: value }), width: "100%", type: "number" }), _jsx(Input, { label: "Mfr Units Per Pack", value: mfrFormData.mfrUnitsPerPack, onChange: (value) => setMfrFormData({ ...mfrFormData, mfrUnitsPerPack: value }), width: "100%", type: "number" })] }), !isNewProduct && (_jsx("div", { className: styles.formActions, children: _jsx(Button, { variant: "primaryV2", size: "small", label: "Save Changes", onClick: handleSaveManufacturerDetails, disabled: isSaving }) }))] })] }), _jsx("div", { className: styles.pricingTableSection, children: _jsx(SimpleTable, { columns: [
                                            {
                                                key: 'corpBuyingGroup',
                                                header: 'Corp/Buying Group',
                                                accessor: 'corpBuyingGroup',
                                                render: (row) => row.id !== NEW_GROUP_PRICE_ID ? (_jsx("div", { className: styles.groupName, children: row.corpBuyingGroup })) : (_jsx(Input, { value: newGroupPriceFormData.corpBuyingGroup, onChange: (value) => setNewGroupPriceFormData({ ...newGroupPriceFormData, corpBuyingGroup: value }), width: "100%", height: 24 })),
                                            },
                                            {
                                                key: 'corpDisc',
                                                header: 'Corp Disc',
                                                accessor: 'corpDisc',
                                                render: (row) => row.id !== NEW_GROUP_PRICE_ID ? (_jsx("div", { className: styles.corpDiscStatus, children: row.corpDisc ? 'Yes' : 'No' })) : (_jsx(Toggle, { checked: newGroupPriceFormData.corpDisc || false, onChange: (value) => setNewGroupPriceFormData({ ...newGroupPriceFormData, corpDisc: value }) })),
                                            },
                                            {
                                                key: 'price',
                                                header: 'Price',
                                                accessor: 'price',
                                                render: (row) => row.id !== NEW_GROUP_PRICE_ID ? (_jsx("div", { className: styles.price, children: editingPriceId === row.id ? (_jsx(Input, { value: editingPriceValue, onChange: (value) => setEditingPriceValue(value), onBlur: () => {
                                                            if (editingPriceValue &&
                                                                parseFloat(editingPriceValue) !== row.price) {
                                                                handleEditGroupPrice(row.id, {
                                                                    price: parseFloat(editingPriceValue),
                                                                });
                                                            }
                                                            setEditingPriceId(null);
                                                            setEditingPriceValue('');
                                                        }, onKeyDown: (e) => {
                                                            if (e.key === 'Enter') {
                                                                if (editingPriceValue &&
                                                                    parseFloat(editingPriceValue) !== row.price) {
                                                                    handleEditGroupPrice(row.id, {
                                                                        price: parseFloat(editingPriceValue),
                                                                    });
                                                                }
                                                                setEditingPriceId(null);
                                                                setEditingPriceValue('');
                                                            }
                                                            else if (e.key === 'Escape') {
                                                                setEditingPriceId(null);
                                                                setEditingPriceValue('');
                                                            }
                                                        }, type: "number", width: "100%", height: 24, autoFocus: true })) : (_jsxs("span", { onClick: () => {
                                                            setEditingPriceId(row.id);
                                                            setEditingPriceValue(String(row.price));
                                                        }, style: { cursor: 'pointer' }, children: ["$", typeof row.price === 'number'
                                                                ? row.price.toFixed(2)
                                                                : parseFloat(row.price).toFixed(2)] })) })) : (_jsx(Input, { value: newGroupPriceFormData.price, onChange: (value) => setNewGroupPriceFormData({ ...newGroupPriceFormData, price: value }), width: "100%", height: 24, type: "number" })),
                                            },
                                            {
                                                key: 'action',
                                                header: 'Action',
                                                render: (row) => row.id !== NEW_GROUP_PRICE_ID ? (_jsx(ActionMenu, { options: [
                                                        {
                                                            label: 'Remove',
                                                            onClick: () => {
                                                                if (confirm(`Remove price for ${row.corpBuyingGroup}?`)) {
                                                                    handleDeleteGroupPrice(row.id);
                                                                }
                                                            },
                                                            color: 'red',
                                                        },
                                                    ] })) : (_jsx(Button, { variant: "primaryV2", size: "small", label: "Add", onClick: handleAddGroupPrice, disabled: !newGroupPriceFormData.corpBuyingGroup || !newGroupPriceFormData.price })),
                                            },
                                        ], data: [...formData.groupPrices, newGroupPriceFormData], tableHeading: "Pricing", rowHeight: "48px", hideTopBorder: true, enableSearch: true }) })] }), _jsx("div", { className: styles.rightSection, children: _jsx("div", { className: styles.statusCard, children: _jsxs("div", { className: styles.cardContent, children: [!isNewProduct && (_jsxs(_Fragment, { children: [_jsx("h2", { className: styles.statusTitle, children: "Status" }), _jsx("div", { className: styles.statusDropdownWrapper, children: _jsxs("div", { className: styles.customDropdown, children: [_jsxs("select", { className: styles.statusDropdown, value: product?.isAvailable ? 'published' : 'unpublished', onChange: (e) => {
                                                                    handleStatusChange(e.target.value);
                                                                }, children: [_jsx("option", { value: "published", children: "Published" }), _jsx("option", { value: "unpublished", children: "Unpublished" })] }), _jsxs("div", { className: styles.dropdownDisplay, children: [_jsx("span", { className: `${styles.statusDot} ${product?.isAvailable ? styles.published : styles.unpublished}` }), _jsx("span", { children: product?.isAvailable ? 'Published' : 'Unpublished' })] })] }) })] })), !isNewProduct && _jsx("div", { className: styles.statusDivider }), _jsx("h2", { className: styles.pricingTitle, children: "Base Pricing and Quantities" }), _jsxs("div", { className: styles.pricingFields, children: [_jsxs("div", { className: styles.twoColumnInputs, children: [_jsx(Input, { label: "Retail Price", value: formData.price, onChange: (value) => setFormData({ ...formData, price: value }), width: "100%", type: "number" }), _jsx(Input, { label: "Cost (Wholesale)", value: formData.cost, onChange: (value) => setFormData({ ...formData, cost: value }), width: "100%", type: "number" })] }), _jsx("div", { className: styles.fullWidthField, children: _jsx(Input, { label: "Stock Quantity", value: formData.stockQuantity.toString(), onChange: (value) => setFormData({ ...formData, stockQuantity: value }), width: "100%", type: "number" }) }), _jsxs("div", { className: styles.twoColumnInputs, children: [_jsx(Input, { label: "Pack Size", value: formData.packSize, onChange: (value) => setFormData({ ...formData, packSize: value }), width: "100%", type: "number" }), _jsx(Input, { label: "Units Per Pack", value: formData.unitsPerPack, onChange: (value) => setFormData({ ...formData, unitsPerPack: value }), width: "100%", type: "number" })] }), _jsxs("div", { className: styles.fullWidthField, children: [_jsx(Input, { label: "Shipping Amount", value: formData.shippingAmount, onChange: (value) => setFormData({ ...formData, shippingAmount: value }), width: "100%", type: "number", min: "0", step: "0.01" }), _jsx("p", { className: styles.fieldHint, children: "Leave blank to use the manufacturer default." })] }), !isNewProduct && (_jsx("div", { className: styles.formActions, children: _jsx(Button, { variant: "primaryV2", size: "small", label: "Save Changes", onClick: handleSavePricing, disabled: isSaving }) }))] }), _jsx("div", { className: styles.pricingDivider }), _jsx("h2", { className: styles.imagesTitle, children: "Images" }), (isNewProduct || product) && (_jsx(ImageUploader, { entityId: isNewProduct ? 'new-product' : product.id, images: isNewProduct ? formData.images : (product.images || []), onImagesChange: (images) => {
                                                if (isNewProduct) {
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        images: images,
                                                    }));
                                                    return;
                                                }
                                                setProduct((prev) => (prev ? { ...prev, images } : prev));
                                            }, uploadImage: uploadImage, associateImage: associateImageWithProduct, setPrimaryImage: setPrimaryImage, deleteImage: deleteImage, isLoading: isSaving || isUploadingImage, deferUpload: isNewProduct })), _jsx("div", { className: styles.categoryDivider }), _jsx("h2", { className: styles.categoryTitle, children: "Category" }), _jsx("div", { className: styles.categorySection, children: _jsx("div", { className: styles.categoryDropdownWrapper, children: _jsxs("div", { className: styles.customDropdown, children: [_jsxs("select", { className: styles.categoryDropdown, value: selectedCategory || '', onChange: (e) => {
                                                                handleCategoryChange(e.target.value);
                                                            }, children: [_jsx("option", { value: "", children: "Select Category" }), categoryOptions.map((category) => (_jsx("option", { value: category.id, children: `${'\u00A0\u00A0'.repeat(category.depth)}${category.name}` }, category.id)))] }), _jsx("div", { className: styles.dropdownDisplay, children: _jsx("span", { children: selectedCategory
                                                                    ? getSelectedCategoryLabel()
                                                                    : 'Select Category' }) })] }) }) })] }) }) })] })] }) }));
};
export default ProductDetails;
